import { API_URL } from "../../helpers/api_helper";
import headers from "../Utility/headers";
import { handleApiError, handleFetchError } from "./helpers";

/**
 * Generic fetch wrapper for API calls
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} body - Request body (optional)
 * @returns {Promise<Object>} { response, results }
 */
export const fetchApi = async (endpoint, method = 'GET', body = null) => {
    const options = {
        method,
        headers: headers()
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'DELETE')) {
        options.body = JSON.stringify(body);
    }

    const response = await fetch(`${API_URL}${endpoint}`, options);
    const results = await response.json();

    return { response, results };
};

/**
 * Generic API call handler with error handling
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {Object} body - Request body (optional)
 * @param {Function} notifyError - Error notification function
 * @param {string} errorMessage - Custom error message
 * @returns {Promise<Object|null>} Response data or null if error
 */
export const apiCall = async (endpoint, method = 'GET', body = null, notifyError, errorMessage = "Failed to load data") => {
    try {
        const { response, results } = await fetchApi(endpoint, method, body);

        if (response.ok) {
            return results.data || results;
        } else {
            handleApiError(response, results, notifyError, errorMessage);
            return null;
        }
    } catch (err) {
        handleFetchError(notifyError, `${errorMessage}. Please check your network connection.`);
        return null;
    }
};

/**
 * Processes API response with full error handling
 * @param {Response} response - Fetch API response
 * @param {Object} results - Parsed JSON response
 * @param {Function} notifyError - Error notification function
 * @param {string} errorMessage - Custom error message
 * @returns {Object|null} Response data or null if error
 */
export const processApiResponse = (response, results, notifyError, errorMessage = "Operation failed") => {
    if (response.ok) {
        return results.data || results;
    } else {
        handleApiError(response, results, notifyError, errorMessage);
        return null;
    }
};

/**
 * Processes API response for delete operations
 * @param {Response} response - Fetch API response
 * @param {Object} results - Parsed JSON response
 * @param {Function} notifySuccess - Success notification function
 * @param {Function} notifyError - Error notification function
 * @param {string} successMessage - Success message
 * @param {string} errorMessage - Error message
 * @returns {boolean} True if successful, false otherwise
 */
export const processDeleteResponse = (response, results, notifySuccess, notifyError, successMessage, errorMessage = "Deletion failed") => {
    if (response.ok) {
        notifySuccess(successMessage);
        return true;
    } else {
        handleApiError(response, results, notifyError, errorMessage);
        return false;
    }
};

